import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import {Company, Theme} from '../../../../types/admin-data.types';
import {PhoneTransformer} from '../../../../services/transformer.service';
import {ManagementCompanyService} from '../services/management-company.service';
import {CustomStyleService} from '../../../../services/custom-style.service';
import { Subject } from 'rxjs/Subject';
import { takeUntil} from 'rxjs/operators';
import {concat} from 'rxjs/observable/concat';

declare const toastr;
declare const swal;

@Component({
    selector: 'company-view',
    templateUrl: 'company-view.component.html',
    styleUrls: ['company-view.component.scss']
})
export class CompanyViewComponent implements OnInit, OnDestroy {
    private ngUnsubscribe = new Subject();
    viewedItem_: Company;
    @Input() set viewedItem(value) {
        this.viewedItem_ = value;
        this.managementCompany.getCompanySettings(this.viewedItem_.id);
    }
    get viewedItem() {
        return this.viewedItem_;
    }
    @Output() onRemove = new EventEmitter();
    @Output() onAddOffice = new EventEmitter();
    @Output() onEditOffice = new EventEmitter<any>();
    @Output() onRemoveOffice = new EventEmitter<number>();
    @Output() onClose = new EventEmitter();

    public activeTab = 'offices';

    public themesList: Array<any> = [];
    public emailSenderSettings = {
        emailSender: '',
        emailSenderName: ''
    };

    autoApproveResidentLoading = false;

    constructor(public managementCompany: ManagementCompanyService,
                private customStyles: CustomStyleService) {
    }

    ngOnInit() {

        this.managementCompany.autoApproveResidentLoading
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((data) => {
                this.autoApproveResidentLoading = data;
            });

        this.themesList = this.managementCompany.getAllThemes();
        this.managementCompany.onToggle.subscribe(() => setTimeout(() => this.getSenderSettings()));
        this.getSenderSettings();
    }

    get company(): Company {
        return this.viewedItem;
    }

    get theme(): Theme {
        return this.managementCompany.getCompanyTheme(this.company.theme);
    }

    update(fieldName, value) {
        this.company[fieldName] = value;
        const data = {};
        data[fieldName] = value;
        this.managementCompany.spinnerShow = true;
        this.managementCompany.update(this.company.id, data).subscribe(() => {
            this.managementCompany.getCompanies();
            this.managementCompany.spinnerShow = false;
            toastr.success('Company successfully updated');
            try {
                const userData = JSON.parse(localStorage.getItem('userData'));
                if (fieldName === 'theme' && userData.company && userData.company.id === this.company.id) {
                    this.customStyles.enableTheme(this.managementCompany.getCompanyTheme(data[fieldName]));
                }
            } catch (err) {
                console.error(err);
            }
        });
    }

    updateAutoApprove(value) {
        this.managementCompany.editCompanySettings(this.company.id, value);
    }

    onActionClick({type, data}) {
        switch (type) {
            case 'removeCompany': {
                this.onRemove.emit();

                break;
            }
            case 'addOffice': {
                this.onAddOffice.emit();

                break;
            }
            case 'editOffice': {
                this.onEditOffice.emit(data);

                break;
            }
            case 'removeOffice': {
                this.onRemoveOffice.emit(data);

                break;
            }
        }
    }

    formatPhone(phone: string) {
        return PhoneTransformer.stringToPhone(phone);
    }

    close() {
        this.viewedItem.checked = false;
        this.onClose.emit();
    }

    getSenderSettings() {
        this.emailSenderSettings = {
            emailSender: '',
            emailSenderName: ''
        };
        const url = `/api/v1/company_service_settings/${this.company.id}`;
        this.managementCompany.api.get('domainEmails', url).subscribe(
            res => {
                this.emailSenderSettings = JSON.parse(res._body);
            }, error => this.managementCompany.api.errorHandler(error, 'Failed to get sender info.')
        );
    }

    updateSenderSettings(data) {
        const url = `/api/v1/company_service_settings/${this.company.id}`;
        const subs$ = [
            this.managementCompany.api.patch('domainEmails', url, data)
        ];

        if (data.hasOwnProperty('emailSender') || data.hasOwnProperty('emailSenderName')) {
            subs$.push(this.managementCompany.api.patch('domainBD', `/v1/company_settings/${this.company.id}.json`, data));
        }

        concat(...subs$).subscribe(
            res => toastr.success('Sender Info was updated successfully.'),
            error => this.managementCompany.api.errorHandler(error, 'Failed to update sender info.')
        )
    }

    updateEmailSender(data, emailInput) {
        if (data.emailSender) {
            if (!data.emailSender.match('[a-zA-Z0-9!#$%&\'*+/=?^_`{|}~-]+(?:\\.[a-zA-Z0-9!#$%&\'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?')) {
                toastr.warning('Email is invalid');
                emailInput.isEditMode = true;
                return;
            }
            const url = `/v1/public/check_sender/${data.emailSender}`;
            this.managementCompany.api.get('domainNotifications', url).subscribe(
                res => {
                    const body = JSON.parse(res._body);
                    if (body.status == 'Success')
                        this.updateSenderSettings(data);
                    else {
                        toastr.error('The entered email is not verified');
                        emailInput.isEditMode = true;
                        swal({
                            title: 'Email is not verified',
                            text: 'We will send confirmation email to your inbox. Please click Yes to proceed, or No if you want to change email.',
                            type: 'warning',
                            showCancelButton: true,
                            confirmButtonColor: '#DD6B55',
                            confirmButtonText: 'Yes',
                            cancelButtonText: 'No',
                            closeOnConfirm: true
                        }, () => this.sendVerificationEmail(data.emailSender));
                    }
                },
                error => {
                    this.managementCompany.api.errorHandler(error, 'Failed to verify entered email.');
                    emailInput.isEditMode = true;
                }
            );
        } else
            this.updateSenderSettings(data);
    }

    sendVerificationEmail(email) {
        const url = '/v1/public/add_sender';
        const data = {email: email};
        this.managementCompany.api.post('domainNotifications', url, data).subscribe(
            () => toastr.success('Verification has been sent successfully'),
            (error) => this.managementCompany.api.errorHandler(error, 'Failed to send verification.')
        );
    }

    companyNotificationTemplateEmail(type: 'invitation'|'reset_password') {
        this.managementCompany.invitationEmailModal = type;
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
}




// WEBPACK FOOTER //
// /app/src/app/views/admin/management-company/company-view/company-view.component.ts